import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isRTL } from './i18n';
import { ErrorBoundary } from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import { registerOnlineSync } from './lib/offlineQueue';
import { QR_CODE_FORMAT_REGEX } from '@tendedloop/shared';
import { useAuthStore } from './stores/authStore';
import LoginPage from './pages/LoginPage';
import ScoutHome from './pages/ScoutHome';
import QRScanner from './pages/QRScanner';
import FeedbackFlow from './pages/FeedbackFlow';
import ProfilePage from './pages/ProfilePage';
import MissionsPage from './pages/MissionsPage';
import BadgesPage from './pages/BadgesPage';
import LeaderboardPage from './pages/LeaderboardPage';
import RewardsPage from './pages/RewardsPage';
import ReferralsPage from './pages/ReferralsPage';
import CollectionPage from './pages/CollectionPage';
import OrganizationsPage from './pages/OrganizationsPage';
import AwardsPage from './pages/AwardsPage';
import ReservePage from './pages/ReservePage';

/**
 * Redirect /:qrCode to /scan/:qrCode when a Scout opens a QR code link
 * (e.g., from native camera scan opening in the PWA context).
 */
function QRCodeRedirect() {
  const { qrCode } = useParams<{ qrCode: string }>();
  if (qrCode && QR_CODE_FORMAT_REGEX.test(qrCode)) {
    // If arriving from visitor CTA, mark for auto-submit (feedback already given)
    const params = new URLSearchParams(window.location.search);
    if (params.get('utm_source') === 'visitor-pwa') {
      localStorage.setItem('scout_autoSubmit', 'true');
    }
    return <Navigate to={`/scan/${qrCode}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const { i18n } = useTranslation();
  const dir = isRTL(i18n.language) ? 'rtl' : 'ltr';
  const { checkAuth, isAuthenticated } = useAuthStore();

  useEffect(() => {
    return registerOnlineSync();
  }, []);

  // Populate scout profile on every app mount (page load/reload)
  // so pages with `if (!scout)` guards render immediately.
  useEffect(() => {
    if (isAuthenticated) {
      checkAuth();
    }
  }, [isAuthenticated, checkAuth]);

  return (
    <div dir={dir} className="min-h-screen">
      <ScrollToTop />
      <OfflineBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<ScoutHome />} />
        <Route path="/scan" element={<QRScanner />} />
        <Route path="/scan/:qrCode" element={<FeedbackFlow />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/missions" element={<MissionsPage />} />
        <Route path="/badges" element={<BadgesPage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/rewards" element={<RewardsPage />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/awards" element={<AwardsPage />} />
        <Route path="/reserve" element={<ReservePage />} />
        <Route path="/:qrCode" element={<QRCodeRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <AppContent />
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
